import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Bot, Send, Plus, Trash2, MessageSquare, Loader2,
  Search, ChevronRight, Sparkles, Clock, Wrench, ArrowLeft, ArrowRight, Home
} from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { NEU } from '../neuStyles';

// ─── Markdown-lite renderer (bold, code, lists, headers) ─────────────────────
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let inCodeBlock = false;
  let codeBuffer = [];
  let listBuffer = [];
  let listType = null;

  function flushList() {
    if (listBuffer.length > 0) {
      const Tag = listType === 'ol' ? 'ol' : 'ul';
      elements.push(
        <Tag key={`list-${elements.length}`} style={{ margin: '8px 0', paddingLeft: 24 }}>
          {listBuffer.map((item, i) => <li key={i} style={{ marginBottom: 2 }}>{formatInline(item)}</li>)}
        </Tag>
      );
      listBuffer = [];
      listType = null;
    }
  }

  function formatInline(str) {
    const parts = [];
    let remaining = str;
    let key = 0;
    const regex = /(\*\*(.+?)\*\*|`(.+?)`|_(.+?)_)/g;
    let match;
    let lastIndex = 0;
    while ((match = regex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        parts.push(remaining.substring(lastIndex, match.index));
      }
      if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
      else if (match[3]) parts.push(<code key={key++} style={{ background: '#f0f0f0', padding: '1px 4px', borderRadius: 3, fontSize: '0.9em' }}>{match[3]}</code>);
      else if (match[4]) parts.push(<em key={key++}>{match[4]}</em>);
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < remaining.length) parts.push(remaining.substring(lastIndex));
    return parts.length > 0 ? parts : str;
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${i}`} style={{
            background: '#1e1e2e', color: '#cdd6f4', padding: 12, borderRadius: 6,
            fontSize: '0.85em', overflowX: 'auto', margin: '8px 0', whiteSpace: 'pre-wrap'
          }}>
            {codeBuffer.join('\n')}
          </pre>
        );
        codeBuffer = [];
        inCodeBlock = false;
      } else {
        flushList();
        inCodeBlock = true;
      }
      continue;
    }
    if (inCodeBlock) { codeBuffer.push(line); continue; }

    if (line.startsWith('### ')) { flushList(); elements.push(<h4 key={`h-${i}`} style={{ margin: '12px 0 4px', fontSize: '0.95em' }}>{formatInline(line.slice(4))}</h4>); continue; }
    if (line.startsWith('## ')) { flushList(); elements.push(<h3 key={`h-${i}`} style={{ margin: '14px 0 6px', fontSize: '1.05em' }}>{formatInline(line.slice(3))}</h3>); continue; }
    if (line.startsWith('# ')) { flushList(); elements.push(<h2 key={`h-${i}`} style={{ margin: '16px 0 8px', fontSize: '1.15em' }}>{formatInline(line.slice(2))}</h2>); continue; }

    if (/^[-*]\s/.test(line.trim())) {
      if (listType === 'ol') flushList();
      listType = 'ul';
      listBuffer.push(line.trim().replace(/^[-*]\s/, ''));
      continue;
    }

    if (/^\d+\.\s/.test(line.trim())) {
      if (listType === 'ul') flushList();
      listType = 'ol';
      listBuffer.push(line.trim().replace(/^\d+\.\s/, ''));
      continue;
    }

    if (/^---+$/.test(line.trim())) { flushList(); elements.push(<hr key={`hr-${i}`} style={{ margin: '12px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />); continue; }

    flushList();
    if (line.trim() === '') {
      if (elements.length > 0) elements.push(<div key={`br-${i}`} style={{ height: 8 }} />);
    } else {
      elements.push(<p key={`p-${i}`} style={{ margin: '4px 0', lineHeight: 1.6 }}>{formatInline(line)}</p>);
    }
  }
  flushList();
  return elements;
}

// ─── Quick Action Buttons ────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'System Overview', prompt: 'Give me a complete system overview — patient count, encounters today, pending labs, open invoices, outstanding balance, and any overdue CRM tasks.' },
  { label: 'Today\'s Schedule', prompt: 'Show me today\'s appointment schedule grouped by provider.' },
  { label: 'Overdue Tasks', prompt: 'What CRM tasks are overdue? List them with assignees and priorities.' },
  { label: 'Billing Status', prompt: 'Give me the current billing summary including A/R aging and any invoices that need attention.' },
  { label: 'Pending Labs', prompt: 'Show me all pending lab results that need review.' },
  { label: 'Provider Productivity', prompt: 'Show provider productivity for the last 30 days — encounters, unique patients, and average per day.' },
];

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminAIPage({ user, onNavigate }) {
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Ref to prevent useEffect from overwriting messages during a send operation
  const isSendingRef = useRef(false);

  // ─── Navigation History (back / forward) ────────────────────────────────────
  // Each entry is a conversation id (or null for the start page)
  const [navHistory, setNavHistory] = useState([null]); // start on welcome page
  const [navIndex, setNavIndex] = useState(0);
  const navLockRef = useRef(false); // prevents nav-triggered activeConvId changes from pushing history

  const canGoBack = navIndex > 0;
  const canGoForward = navIndex < navHistory.length - 1;

  const pushNav = useCallback((convId) => {
    setNavHistory(prev => {
      const trimmed = prev.slice(0, navIndex + 1); // discard forward stack
      // Avoid duplicates at top of stack
      if (trimmed[trimmed.length - 1] === convId) return trimmed;
      return [...trimmed, convId];
    });
    setNavIndex(prev => prev + 1);
  }, [navIndex]);

  const handleNavBack = useCallback(() => {
    if (!canGoBack) return;
    const newIdx = navIndex - 1;
    navLockRef.current = true;
    setNavIndex(newIdx);
    const target = navHistory[newIdx];
    isSendingRef.current = false;
    setActiveConvId(target);
    if (!target) setMessages([]);
  }, [canGoBack, navIndex, navHistory]);

  const handleNavForward = useCallback(() => {
    if (!canGoForward) return;
    const newIdx = navIndex + 1;
    navLockRef.current = true;
    setNavIndex(newIdx);
    const target = navHistory[newIdx];
    isSendingRef.current = false;
    setActiveConvId(target);
    if (!target) setMessages([]);
  }, [canGoForward, navIndex, navHistory]);

  const handleGoHome = useCallback(() => {
    if (activeConvId === null && messages.length === 0) return; // already home
    pushNav(null);
    isSendingRef.current = false;
    setActiveConvId(null);
    setMessages([]);
  }, [activeConvId, messages.length, pushNav]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const convs = await window.zignagenixAPI.getAdminAIConversations();
      if (!convs.error) setConversations(convs);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Load messages when conversation changes (but NOT during active sends)
  useEffect(() => {
    if (!activeConvId) { setMessages([]); return; }

    // Skip loading from DB if we're in the middle of sending a message
    // (handleSend manages messages optimistically during sends)
    if (isSendingRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const msgs = await window.zignagenixAPI.getAdminAIMessages(activeConvId);
        if (!cancelled && !msgs.error) setMessages(msgs);
      } catch (err) {
        console.error('Failed to load messages:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeConvId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Create new conversation
  const handleNewConversation = useCallback(async () => {
    try {
      const conv = await window.zignagenixAPI.createAdminAIConversation();
      if (conv && !conv.error) {
        isSendingRef.current = false; // Allow message loading for sidebar-initiated conversations
        pushNav(conv.id);
        setActiveConvId(conv.id);
        setMessages([]);
        await loadConversations();
        inputRef.current?.focus();
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
    }
  }, [loadConversations]);

  // Delete conversation
  const handleDeleteConversation = useCallback(async (id, e) => {
    e.stopPropagation();
    try {
      await window.zignagenixAPI.deleteAdminAIConversation(id);
      if (activeConvId === id) {
        setActiveConvId(null);
        setMessages([]);
      }
      await loadConversations();
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [activeConvId, loadConversations]);

  // Send message
  const handleSend = useCallback(async (text) => {
    const msgText = (typeof text === 'string' ? text : '') || inputText.trim();
    if (!msgText || isLoading) return;

    let convId = activeConvId;

    // Auto-create conversation if none active
    if (!convId) {
      try {
        const conv = await window.zignagenixAPI.createAdminAIConversation(msgText.substring(0, 60));
        if (!conv || conv.error) {
          // Show error to user instead of silently returning
          const errMsg = conv?.message || 'Failed to create conversation. Please try again.';
          setMessages([{
            role: 'assistant',
            content: `Error: ${errMsg}`,
            created_at: new Date().toISOString()
          }]);
          return;
        }
        convId = conv.id;
        // Set the flag BEFORE setting activeConvId to prevent useEffect from wiping messages
        isSendingRef.current = true;
        pushNav(convId);
        setActiveConvId(convId);
      } catch (err) {
        console.error('Failed to create conversation:', err);
        setMessages([{
          role: 'assistant',
          content: `Connection error: ${err.message}. Please check your connection and try again.`,
          created_at: new Date().toISOString()
        }]);
        return;
      }
    }

    // Add user message to UI immediately
    const userMsg = { role: 'user', content: msgText, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInputText('');
    setIsLoading(true);

    try {
      const result = await window.zignagenixAPI.sendAdminAIMessage(convId, msgText);
      if (result.error) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Error: ${result.message || 'Something went wrong. Please try again.'}`,
          created_at: new Date().toISOString()
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: result.response,
          tool_calls_json: result.toolCalls?.length > 0 ? JSON.stringify(result.toolCalls) : null,
          created_at: new Date().toISOString()
        }]);
      }
      await loadConversations();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Connection error: ${err.message}. Please check your API key configuration.`,
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
      // Clear the sending flag so future conversation switches load from DB normally
      isSendingRef.current = false;
    }
  }, [inputText, isLoading, activeConvId, loadConversations]);

  // Select conversation from sidebar
  const handleSelectConversation = useCallback((id) => {
    isSendingRef.current = false; // Allow normal message loading
    pushNav(id);
    setActiveConvId(id);
  }, [pushNav]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100%', background: '#F8F9FA', overflow: 'hidden' }}>

      {/* ── Conversation Sidebar ────────────────────────────────── */}
      <div style={{
        width: sidebarOpen ? 280 : 0, minWidth: sidebarOpen ? 280 : 0,
        background: '#F8F9FA', borderRight: 'none', boxShadow: 'inset -1px 0 0 rgba(186,199,216,0.45)',
        display: 'flex', flexDirection: 'column', transition: 'width 0.2s, min-width 0.2s',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '16px 12px', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)' }}>
          <button onClick={handleNewConversation} style={{
            width: '100%', padding: '10px 16px', background: '#4f46e5', color: '#fff',
            border: 'none', borderRadius: 8, cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600,
          }}>
            <Plus size={16} /> New Conversation
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {conversations.length === 0 && (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              No conversations yet. Start one!
            </div>
          )}
          {conversations.map(conv => (
            <div key={conv.id} onClick={() => handleSelectConversation(conv.id)} style={{
              padding: '10px 12px', margin: '2px 8px', borderRadius: 8, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              background: activeConvId === conv.id ? '#eef2ff' : 'transparent',
              color: activeConvId === conv.id ? '#4f46e5' : '#374151',
            }}>
              <MessageSquare size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
              <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {conv.title}
              </span>
              <button onClick={(e) => handleDeleteConversation(conv.id, e)} style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                color: '#9ca3af', opacity: 0, transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0'}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Chat Area ─────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* Header */}
        <div style={{
          padding: '14px 20px', background: '#F8F9FA', borderBottom: 'none', boxShadow: 'inset 0 -1px 0 rgba(186,199,216,0.45)',
          display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6C757D', boxShadow: 'none',
            }}>
              <ChevronRight size={18} style={{ transform: sidebarOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #4f46e5, #2E8B98)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Bot size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>Admin AI Assistant</div>
              <div style={{ fontSize: 12, color: '#6C757D' }}>Powered by Claude &middot; Full system access</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* Back */}
            <button onClick={handleNavBack} disabled={!canGoBack} title="Go back"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, cursor: canGoBack ? 'pointer' : 'default',
                background: canGoBack ? undefined : 'transparent',
                color: canGoBack ? '#4f46e5' : '#ccc',
                boxShadow: canGoBack ? undefined : 'none', transition: 'all 0.15s',
              }}
            >
              <ArrowLeft size={17} />
            </button>
            {/* Forward */}
            <button onClick={handleNavForward} disabled={!canGoForward} title="Go forward"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, cursor: canGoForward ? 'pointer' : 'default',
                background: canGoForward ? undefined : 'transparent',
                color: canGoForward ? '#4f46e5' : '#ccc',
                boxShadow: canGoForward ? undefined : 'none', transition: 'all 0.15s',
              }}
            >
              <ArrowRight size={17} />
            </button>
            <div style={{ width: 1, height: 22, background: 'rgba(186,199,216,0.45)', margin: '0 4px' }} />
            {/* Home — return to AI start page */}
            <button onClick={handleGoHome} title="Admin AI start page"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 34, height: 34, cursor: 'pointer',
                background: (!activeConvId && messages.length === 0) ? '#EEF2FF' : undefined,
                color: '#4f46e5',
                transition: 'all 0.15s',
              }}
            >
              <Home size={17} />
            </button>
            <div style={{ width: 1, height: 22, background: 'rgba(186,199,216,0.45)', margin: '0 4px' }} />
            <PrintButton pageTitle="Admin AI Assistant" subtitle="System Queries & Administration" />
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {messages.length === 0 && !isLoading ? (
            /* Welcome Screen */
            <div style={{ maxWidth: 640, margin: '40px auto', textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16, margin: '0 auto 20px',
                background: 'linear-gradient(135deg, #4f46e5, #2E8B98)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Sparkles size={32} color="#fff" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                How can I help you today?
              </h2>
              <p style={{ color: '#6C757D', fontSize: 14, marginBottom: 28, lineHeight: 1.5 }}>
                I can look up patients, check billing, review schedules, manage CRM tasks,
                analyze provider productivity, and more. Just ask in plain English.
              </p>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 10, textAlign: 'left',
              }}>
                {QUICK_ACTIONS.map((action, i) => (
                  <button key={i} onClick={() => handleSend(action.prompt)} style={{
                    padding: '12px 14px', borderRadius: 12,
                    cursor: 'pointer', fontSize: 13, color: '#374151',
                    textAlign: 'left', transition: 'all 0.15s',
                    fontFamily: 'inherit',
                  }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>{action.label}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.3 }}>{action.prompt.substring(0, 60)}...</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* Message List */
            <>
              {messages.map((msg, i) => (
                <div key={i} style={{
                  display: 'flex', gap: 12, marginBottom: 20,
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  maxWidth: 800, marginLeft: msg.role === 'user' ? 'auto' : 0,
                  marginRight: msg.role === 'user' ? 0 : 'auto',
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: 32, height: 32, borderRadius: 12, flexShrink: 0,
                    background: msg.role === 'user'
                      ? '#e0e7ff'
                      : 'linear-gradient(135deg, #4f46e5, #2E8B98)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: NEU.shadow.xs,
                  }}>
                    {msg.role === 'user'
                      ? <Search size={14} color="#4f46e5" />
                      : <Bot size={14} color="#fff" />}
                  </div>

                  {/* Content */}
                  <div style={{
                    flex: 1, minWidth: 0, padding: '12px 16px', borderRadius: 12,
                    background: msg.role === 'user' ? '#4f46e5' : '#F8F9FA',
                    color: msg.role === 'user' ? '#fff' : '#1f2937',
                    border: 'none', boxShadow: msg.role === 'user' ? NEU.shadow.sm : NEU.shadow.xs,
                    fontSize: 14, lineHeight: 1.6,
                  }}>
                    {msg.role === 'user' ? (
                      <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                    ) : (
                      <div>{renderMarkdown(msg.content)}</div>
                    )}

                    {/* Tool calls indicator */}
                    {msg.tool_calls_json && (() => {
                      try {
                        const tools = JSON.parse(msg.tool_calls_json);
                        if (tools.length > 0) {
                          return (
                            <div style={{
                              marginTop: 10, paddingTop: 10, borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)',
                              display: 'flex', flexWrap: 'wrap', gap: 6,
                            }}>
                              <Wrench size={12} color="#9ca3af" style={{ marginTop: 2 }} />
                              {tools.map((t, j) => (
                                <span key={j} style={{
                                  fontSize: 11, background: '#F8F9FA', padding: '2px 8px',
                                  borderRadius: 6, color: '#6C757D', boxShadow: NEU.shadow.xs,
                                }}>
                                  {t.name.replace(/_/g, ' ')}
                                </span>
                              ))}
                            </div>
                          );
                        }
                      } catch { return null; }
                      return null;
                    })()}

                    {/* Timestamp */}
                    <div style={{
                      fontSize: 10, color: msg.role === 'user' ? 'rgba(255,255,255,0.6)' : '#9ca3af',
                      marginTop: 6,
                    }}>
                      <Clock size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />
                      {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, maxWidth: 800 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 12, flexShrink: 0,
                    background: 'linear-gradient(135deg, #4f46e5, #2E8B98)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: NEU.shadow.xs,
                  }}>
                    <Bot size={14} color="#fff" />
                  </div>
                  <div style={{
                    padding: '14px 18px', borderRadius: 12, background: '#F8F9FA',
                    border: 'none', boxShadow: NEU.shadow.xs, display: 'flex', alignItems: 'center', gap: 10,
                    color: '#6C757D', fontSize: 14,
                  }}>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Analyzing your request...
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input Area */}
        <div style={{ padding: '16px 24px', background: '#F8F9FA', borderTop: 'none', boxShadow: 'inset 0 1px 0 rgba(186,199,216,0.45)' }}>
          <div style={{
            display: 'flex', alignItems: 'flex-end', gap: 10,
            background: '#F8F9FA', border: 'none', borderRadius: 12, padding: '8px 12px', boxShadow: NEU.shadow.insetSm,
          }}>
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask me anything about your system..."
              rows={1}
              style={{
                flex: 1, border: 'none', background: 'transparent', resize: 'none',
                fontSize: 14, lineHeight: 1.5, outline: 'none', padding: '4px 0',
                minHeight: 24, maxHeight: 120, fontFamily: 'inherit',
              }}
              onInput={e => {
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={() => handleSend()}
              disabled={!inputText.trim() || isLoading}
              style={{
                width: 36, height: 36, cursor: 'pointer',
                background: inputText.trim() && !isLoading ? '#4f46e5' : '#e5e7eb',
                color: inputText.trim() && !isLoading ? '#fff' : '#9ca3af',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                transition: 'all 0.15s', boxShadow: inputText.trim() && !isLoading ? undefined : 'none',
              }}
            >
              <Send size={16} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, textAlign: 'center' }}>
            Admin AI uses Claude to query your system in real time. All actions are audit-logged.
          </div>
        </div>
      </div>

      {/* Spin animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
